import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { authStore, otpStart, otpVerify } from '@/lib/api/auth'
import type { ApiError } from '@/lib/api/client'

const phoneRe = /^\+998\d{9}$/u
const codeRe = /^\d{6}$/u

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [stage, setStage] = useState<'phone' | 'otp'>('phone')
  const [phone, setPhone] = useState('+998')
  const [requestId, setRequestId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submitPhone(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!phoneRe.test(phone)) {
      setError(t('login.phone_invalid'))
      return
    }
    setBusy(true)
    try {
      const res = await otpStart(phone)
      setRequestId(res.requestId)
      setStage('otp')
    } catch (err) {
      setError((err as ApiError).message ?? t('login.error'))
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!requestId) return
    if (!codeRe.test(code)) {
      setError(t('login.code_invalid'))
      return
    }
    setBusy(true)
    try {
      const res = await otpVerify(requestId, code)
      authStore.set(res.token, res.userId)
      navigate({ to: '/map' })
    } catch (err) {
      setError((err as ApiError).message ?? t('login.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <h1 className="mb-6 text-2xl font-semibold">{t('login.title')}</h1>

      {stage === 'phone' && (
        <form onSubmit={submitPhone} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t('login.phone')}</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.trim())}
              placeholder="+998901234567"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono focus:border-sky-500 focus:outline-none"
              autoComplete="tel"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-sky-600 py-2.5 font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {busy ? t('login.sending') : t('login.send_code')}
          </button>
        </form>
      )}

      {stage === 'otp' && (
        <form onSubmit={submitCode} className="space-y-4">
          <p className="text-sm text-slate-600">
            {t('login.otp_sent_to', { phone })}
          </p>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t('login.code')}</span>
            <input
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center font-mono text-lg tracking-widest focus:border-sky-500 focus:outline-none"
              autoComplete="one-time-code"
            />
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setStage('phone')
                setCode('')
                setRequestId(null)
                setError(null)
              }}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t('login.change_phone')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 rounded-lg bg-sky-600 py-2.5 font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {busy ? t('login.verifying') : t('login.verify')}
            </button>
          </div>
        </form>
      )}

      {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
    </section>
  )
}
